import { defineStore } from "pinia";
import { ref, computed } from "vue";
import ApiManager from "~/code/api-manager";
import { EventBus } from "~/code/event-bus";
import { useErrorHandler } from "~/composables/useErrorHandler";
import { ServerEnvironment } from "~/generated";
import { useSidebarItemStore } from "~/store/sidebar-item-store";
import { PermissionPrefix } from "~/store/permission-prefix";
import { useUserStore } from "~/store/user-store";
import { normalizePermissionToken, resolveRoutePermissionPrefixes } from "~/code/server-scope";

export const useServerEnvironmentStore = defineStore("serverEnvironmentStore", {
  state: () => ({
    allServerEnvironments: [] as ServerEnvironment[],
    allPermissionPrefixes: [] as PermissionPrefix[],
    isInitialized: false,
    isInitializing: false,
    initPromise: null as Promise<void> | null,
    permissionScopeKey: "",
    apiManager: ApiManager.getInstance().apis["ServerEnvironmentApi"],
    errorHandler: useErrorHandler(),
  }),
  actions: {
    normalizePermissionPrefix(permissionPrefix: string) {
      return normalizePermissionToken(permissionPrefix);
    },

    getPermissionPrefixCandidates(permissionPrefix: string) {
      const normalized = this.normalizePermissionPrefix(permissionPrefix);
      const candidates = new Set<string>([normalized]);

      if (normalized.endsWith("-server")) {
        candidates.add(normalized.slice(0, -"-server".length));
      } else {
        candidates.add(`${normalized}-server`);
      }

      return Array.from(candidates);
    },

    isGenericPermissionPrefix(permissionPrefix: string) {
      const normalized = this.normalizePermissionPrefix(permissionPrefix);
      return normalized === "edit-content" || normalized === "edit-content-server";
    },

    async getRoutePermissionPrefixes() {
      await this.init();

      const sidebarItemStore = useSidebarItemStore();

      if ((sidebarItemStore.sidebarTree || []).length === 0) {
        await sidebarItemStore.fetchSidebarTree();
      }

      return resolveRoutePermissionPrefixes({
        routePath:
          typeof window === "undefined" ? "" : window.location.pathname || "",
        sidebarTree: sidebarItemStore.sidebarTree || [],
        userPermissionEntries: await this.getEffectiveUserPermissionEntries(),
        configuredPermissionPrefixes: (this.allPermissionPrefixes || []).map(
          (prefix) => prefix?.name || ""
        ),
      });
    },

    async resolvePermissionPrefixes(permissionPrefix: string | string[]) {
      const requestedPrefixes = Array.isArray(permissionPrefix)
        ? permissionPrefix
        : [permissionPrefix];
      const needsRouteScope = requestedPrefixes.some((prefix) =>
        this.isGenericPermissionPrefix(prefix)
      );
      const routePermissionPrefixes = needsRouteScope
        ? await this.getRoutePermissionPrefixes()
        : [];

      return Array.from(
        new Set(
          requestedPrefixes.flatMap((prefix) =>
            this.isGenericPermissionPrefix(prefix)
              ? routePermissionPrefixes
              : this.getPermissionPrefixCandidates(prefix)
          )
        )
      );
    },

    findPermissionPrefix(permissionPrefix: string) {
      const candidates = this.getPermissionPrefixCandidates(permissionPrefix);

      return this.allPermissionPrefixes.find((prefix) =>
        candidates.includes(prefix.name.toLowerCase())
      );
    },

    getLocalStorageUser() {
      const userJson = localStorage.getItem("user") || sessionStorage.getItem("user");

      if (!userJson) {
        return null;
      }

      try {
        return JSON.parse(userJson);
      } catch (_error) {
        return null;
      }
    },

    restorePrefixSelection(prefix: PermissionPrefix) {
      const stored = localStorage.getItem(`server-${prefix.name}`);
      if (!stored) {
        return;
      }

      const selection = JSON.parse(stored);
      if (
        prefix.myPermissionedOptions.map((option) => option.name).includes(selection)
      ) {
        prefix.mySelection = selection;
      }
    },

    async getEffectiveUserPermissionEntries() {
      const parsedUser = this.getLocalStorageUser();

      if (!parsedUser) {
        return [] as string[];
      }

      const userRoles = (parsedUser.roles || []).map((role: any) =>
        String(typeof role === "string" ? role : role?.name || "").toLowerCase()
      );
      const isAdminUser = userRoles.includes("admin");
      const impersonatedRole =
        (localStorage.getItem("auth-impersonated-role") || "").trim();
      const impersonatedRoleIsAdmin =
        impersonatedRole.length > 0 && impersonatedRole.toLowerCase() === "admin";
      const isImpersonating =
        isAdminUser && impersonatedRole.length > 0 && !impersonatedRoleIsAdmin;

      if (isAdminUser && (!isImpersonating || impersonatedRoleIsAdmin)) {
        return ["__admin_full_access__"];
      }

      if (isImpersonating) {
        const userStore = useUserStore();

        if (userStore.roles.length === 0) {
          await userStore.fetchRoles(1, 500, "name", "asc", null);
        }

        const matchedRole = userStore.roles.find(
          (role) =>
            role.name?.trim().toLowerCase() === impersonatedRole.toLowerCase()
        );

        if (!matchedRole) {
          return [];
        }

        return this.resolveAssignedPermissions(matchedRole.rolePermissions || []);
      }

      return this.resolveAssignedPermissions(parsedUser.permissions || []);
    },

    async resolveAssignedPermissions(entries: unknown[]) {
      const userStore = useUserStore();
      await userStore.ensurePermissionsLoaded();
      const names = entries.map((entry: any) => {
        const value = typeof entry === "object" && entry !== null
          ? entry.permissionName || entry.permission?.name || entry.name || entry.permissionId || entry.permission?.id || entry.id
          : entry;
        if (typeof value === "string" && value.includes(":")) return value.trim();
        if (typeof value === "number" || (typeof value === "string" && /^\d+$/.test(value.trim()))) {
          return userStore.permissionNameById(Number(value));
        }
        return typeof value === "string" ? value.trim() : null;
      });
      return Array.from(new Set(names.filter((name): name is string => Boolean(name))));
    },

    async getAccessibleServerOptions() {
      await this.init();
      await this.applyPermissionScope();
      const servers = new Map<string, ServerEnvironment>();
      this.allPermissionPrefixes.forEach((prefix) => prefix.myPermissionedOptions.forEach((server) => {
        if (server.name) servers.set(server.name, server);
      }));
      return this.sortServers(Array.from(servers.values()));
    },

    async applyPermissionScope(force = false) {
      const parsedUser = this.getLocalStorageUser();
      const userRoles = (parsedUser?.roles || []).map((role: any) =>
        String(typeof role === "string" ? role : role?.name || "").toLowerCase()
      );
      const isAdminUser = userRoles.includes("admin");
      const impersonatedRole =
        (localStorage.getItem("auth-impersonated-role") || "").trim();
      const impersonatedRoleIsAdmin =
        impersonatedRole.length > 0 && impersonatedRole.toLowerCase() === "admin";
      const isImpersonating =
        isAdminUser && impersonatedRole.length > 0 && !impersonatedRoleIsAdmin;
      const permissions = await this.getEffectiveUserPermissionEntries();

      const scopeKey = JSON.stringify({
        impersonatedRole: impersonatedRole.toLowerCase(),
        isAdminOverride: isAdminUser && !isImpersonating,
        permissions: [...permissions].sort((a, b) => a.localeCompare(b)),
      });

      if (!force && this.permissionScopeKey === scopeKey) {
        return;
      }

      this.allPermissionPrefixes.forEach((prefix) => {
        prefix.myPermissionedOptions = [];
        prefix.mySelection = null;
      });

      if (isAdminUser && (!isImpersonating || impersonatedRoleIsAdmin)) {
        this.allPermissionPrefixes.forEach((prefix) => {
          prefix.myPermissionedOptions = [...this.allServerEnvironments];
          this.restorePrefixSelection(prefix);
        });

        this.permissionScopeKey = scopeKey;
        return;
      }

      if (permissions.includes("__admin_full_access__")) {
        this.allPermissionPrefixes.forEach((prefix) => {
          prefix.myPermissionedOptions = [...this.allServerEnvironments];
          this.restorePrefixSelection(prefix);
        });

        this.permissionScopeKey = scopeKey;
        return;
      }

      permissions.forEach((permissionEntry: string) => {
        const parts = permissionEntry.split(":");

        if (parts.length < 2) {
          return;
        }

        const permissionType = this.normalizePermissionPrefix(parts[0]);
        const serverName = parts.slice(1).join(":").trim().toLowerCase();
        const withUnderscores = serverName.replace(/-/g, "_");
        const withHyphens = serverName.replace(/_/g, "-");

        let prefix = this.allPermissionPrefixes.find(
          (entry) => this.normalizePermissionPrefix(entry.name) === permissionType
        );

        if (!prefix) {
          prefix = this.allPermissionPrefixes.find((entry) =>
            entry.name.toLowerCase().includes(permissionType)
          );
        }

        if (!prefix) {
          prefix = {
            name: permissionType,
            myPermissionedOptions: [],
            mySelection: null,
          };
          this.allPermissionPrefixes.push(prefix);
        }

        const matchedServer = this.allServerEnvironments.find((environment) => {
          const environmentName = environment.name?.toLowerCase();

          return (
            environmentName === serverName ||
            environmentName === withUnderscores ||
            environmentName === withHyphens
          );
        });

        if (
          matchedServer &&
          !prefix.myPermissionedOptions.some(
            (option) => option.name === matchedServer.name
          )
        ) {
          prefix.myPermissionedOptions.push(matchedServer);
        }
      });

      this.allPermissionPrefixes.forEach((prefix) => {
        prefix.myPermissionedOptions = this.sortServers(prefix.myPermissionedOptions);
        this.restorePrefixSelection(prefix);
      });

      this.permissionScopeKey = scopeKey;
    },

    async init() {
      if (this.isInitialized) return;
      if (this.isInitializing && this.initPromise) {
        await this.initPromise;
        return;
      }

      this.isInitializing = true;
      this.initPromise = (async () => {
        try {
          const prefixesResult =
            await this.apiManager.apiServerConfigAllPermissionPrefixesGet();
          this.allPermissionPrefixes = prefixesResult.data.map((item: string) => ({
            name: item,
            myPermissionedOptions: [],
            mySelection: null,
          }));

          const envResult =
            await this.apiManager.apiServerConfigAllServersGet();
          this.allServerEnvironments = (envResult.data || []).sort((a: ServerEnvironment, b: ServerEnvironment) => {
            const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
            const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
            return orderA - orderB;
          });

          await this.applyPermissionScope(true);

          this.isInitialized = true;
        } catch (e) {
          console.error("Failed to initialize server environment:", e);
          throw e;
        } finally {
          this.isInitializing = false;
        }
      })();

      await this.initPromise;
    },
    reset() {
      this.allServerEnvironments = [];
      this.allPermissionPrefixes = [];
      this.isInitialized = false;
      this.isInitializing = false;
      this.initPromise = null;
    },
    async addOrEditServerEnvironment(server: ServerEnvironment) {
      await this.init();
      try {
        var response;
        //make sure projectid is int
        if (typeof server.projectId === "string") {
          server.projectId = parseInt(server.projectId, 10);
        }
        //there's one in the project too
        if (server.project && typeof server.project.id === "string") {
          server.project.id = parseInt(server.project.id, 10);
        }

        if (!server.id) {
          server.id = "00000000-0000-0000-0000-000000000000";

          response = await this.apiManager.apiServerConfigServerEnvironmentPut(
            server
          );
        } else {
          // Edit existing server environment
          response = await this.apiManager.apiServerConfigServerEnvironmentPost(
            server
          );
        }
        const existingIndex = this.allServerEnvironments.findIndex(
          (s) => s.id === server.id
        );
        if (existingIndex !== -1) {
          this.allServerEnvironments.splice(existingIndex, 1, response.data);
        }
        // If the server was not found, add it to the list
        else {
          this.allServerEnvironments.push(response.data);
        }
      } catch (error: unknown) {
        console.error("Error adding or editing server environment:", error);
        const message = error instanceof Error ? error.message : String(error);
        EventBus.emit("toast", {
          type: "error",
          message: `Error adding or editing server environment: ${message}`,
        });
      }
      this.errorHandler.handleErrors(response, "Server Environment Error");
    },

    async fetchAllServerEnvironments() {
      const response = await this.apiManager.apiServerConfigAllServersGet();
      this.allServerEnvironments = (response.data || []).sort((a: ServerEnvironment, b: ServerEnvironment) => {
        const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
        const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });
      return this.allServerEnvironments;
    },

    async getPermissions(server: string) {
      await this.init();
      await this.applyPermissionScope();
      const perms = this.allPermissionPrefixes.filter((p) =>
        p.myPermissionedOptions.some(
          (opt) => opt.name?.toLowerCase() === server.toLowerCase()
        )
      );
      return perms.map((p) => p.name);
    },

    async getServerOptions(permissionPrefix: string | string[]) {
      await this.init();
      await this.applyPermissionScope();
      let list: ServerEnvironment[] = [];
      const resolvedPrefixes = await this.resolvePermissionPrefixes(permissionPrefix);

      resolvedPrefixes.forEach((perm) => {
        const prefix = this.findPermissionPrefix(perm);
        if (prefix) {
          list.push(...prefix.myPermissionedOptions);
        }
      });

      return Array.from(new Map(list.map((i) => [i.name, i])).values());
    },

    async setServer(server: string, permissionPrefix: string | string[]) {
      await this.init();
      await this.applyPermissionScope();
      const resolvedPrefixes = await this.resolvePermissionPrefixes(permissionPrefix);
      const apply = (perm: string) => {
        const normalized = this.normalizePermissionPrefix(perm);
        const prefix = this.findPermissionPrefix(perm);
        if (prefix) {
          prefix.mySelection = server;
          localStorage.setItem(
            `server-${prefix.name.toLowerCase()}`,
            JSON.stringify(server)
          );
          localStorage.setItem(`server-${normalized}`, JSON.stringify(server));
        }
      };
      resolvedPrefixes.forEach(apply);
      this.$patch({});
    },

    async getServer(permissionPrefix: string | string[]) {
      await this.init();
      await this.applyPermissionScope();
      const resolvedPrefixes = await this.resolvePermissionPrefixes(permissionPrefix);

      for (const perm of resolvedPrefixes) {
        const prefix = this.findPermissionPrefix(perm);
        if (prefix && prefix.mySelection) {
          return prefix.mySelection;
        }
      }

      return null;
    },

    sortServers(servers: ServerEnvironment[]) {
      const order: Record<string, number> = this.allServerEnvironments.reduce(
        (acc, env, idx) => {
          acc[env.name?.toLowerCase() || ""] = idx;
          return acc;
        },
        {} as Record<string, number>
      );
      return servers.sort(
        (a, b) =>
          (order[a.name?.toLowerCase() || ""] ?? Infinity) -
          (order[b.name?.toLowerCase() || ""] ?? Infinity)
      );
    },
  },
});
